import { OnInit, Inject, Component, ViewEncapsulation, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { AttributeType } from 'src/shared/enums';
import { LinksValidator } from '../../../../shared/zoneiqui/components/links/links.validator';

import * as _ from 'lodash';
import ICalibrationSetAttribute = ZoneIQCalibration.ICalibrationSetAttribute;
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { IAttributeModel } from 'src/app/interfaces/attribute-model.interface';
import { IAttributeService } from 'src/app/interfaces/services/attribute-service.interface';
import { emptyStringValidator } from 'src/shared/zoneiqui/components/links/empty-string.validator';
import { CommentComponent } from '../../../../shared/zoneiqui/components/comment/comment.component';

@Component({
    standalone: false,
    selector: 'attributes-popup',
    templateUrl: './attributes-popup.component.html',
    styleUrls: ['./attributes-popup.component.scss'],
    encapsulation: ViewEncapsulation.None

})
export class AttributesPopupComponent implements OnInit {
    linkForm: UntypedFormGroup;
    dialogReference: any;
    disabled = false;
    protected attributeService: IAttributeService<IAttributeModel>;

    @ViewChild(CommentComponent)
    commentComponent: CommentComponent;

    constructor(@Inject(MAT_DIALOG_DATA) public data: any, protected readonly formBuilder: UntypedFormBuilder) {}

    private readonly linksSubject: BehaviorSubject<IAttributeModel[]> = new BehaviorSubject<IAttributeModel[]>([]);
    private readonly commentsSubject: BehaviorSubject<IAttributeModel[]> = new BehaviorSubject<IAttributeModel[]>([]);
    private readonly assumptionsSubject: BehaviorSubject<IAttributeModel[]> = new BehaviorSubject<IAttributeModel[]>([]);

    links: Observable<IAttributeModel[]> = this.linksSubject.asObservable();
    comments: Observable<IAttributeModel[]> = this.commentsSubject.asObservable();
    assumptions: Observable<IAttributeModel[]> = this.assumptionsSubject.asObservable();

    protected attributesIdsToDelete: number[] = [];
    protected attributesToAddOrUpdate: IAttributeModel[] = [];

    protected get linksValue(): IAttributeModel[] {
        return this.linksSubject.value;
    }

    protected get commentsValue(): IAttributeModel[] {
        return this.commentsSubject.value;
    }

    protected get assumptionsValue(): IAttributeModel[] {
        return this.assumptionsSubject.value;
    }

    protected setLinks(links: IAttributeModel[]) {
        this.linksSubject.next(links);
    }

    protected setComments(comments: IAttributeModel[]) {
        this.commentsSubject.next(comments);
    }

    protected setAssumptions(assumptions: IAttributeModel[]) {
        this.assumptionsSubject.next(assumptions);
    }

    // currently one comment
    get comment() {
        return _.get(this.commentsValue, '0', null);
    }

    get assumption(): IAttributeModel {
        return _.get(this.assumptionsValue, '0', {}) as any as IAttributeModel;
    }

    get canSave() {
        return (
            !this.linksValue
                .concat(this.commentsValue)
                .concat(this.assumptionsValue)
                .filter(attribute => !!attribute)
                .find(attribute => !attribute.isValid) &&
            (!this.isNewLinkFilled || (this.isNewLinkFilled && this.isNewLinkCorrect))
        );
    }

    get isNewLinkFilled() {
        return !!_.get(this.linkForm.value, 'linkValue') || !!_.get(this.linkForm.value, 'linkDescription');
    }

    get isNewLinkCorrect() {
        return !!_.get(this.linkForm, 'valid');
    }

    get popupTitle(): string {
        return this.data.title;
    }

    ngOnInit() {
        this.linkForm = this.formBuilder.group({
            linkValue: new UntypedFormControl('', [LinksValidator.linkValidator]),
            linkDescription: new UntypedFormControl('', [
                emptyStringValidator('linkDescription'),
                Validators.maxLength(250),
                Validators.required
            ])
        });
    }

    protected initAttributes(allAttributes: IAttributeModel[]) {
        const links = [...this.linksValue];
        const comments = [...this.commentsValue];
        const assumptions = [...this.assumptionsValue];

        allAttributes.forEach(attribute => {
            switch (attribute.AttributeType) {
                case AttributeType.LINK:
                    links.push(attribute);
                    break;
                case AttributeType.COMMENT:
                    comments.push(attribute);
                    break;
                case AttributeType.ASSUMPTION:
                    assumptions.push(attribute);
                    break;
            }
        });

        this.setLinks(links);
        this.setComments(comments);
        this.setAssumptions(assumptions);

        this.initDefaultAssumption();
        this.initDefaultComment();
    }

    addParentId(list: ICalibrationSetAttribute[], Id: number) {
        list.forEach((item: ICalibrationSetAttribute) => {
            item.ParentId = Id;
        });
    }

    addNewLink(link?: IAttributeModel) {
        this.setLinks(this.linksValue.concat(link));
        this.linkForm.controls.linkValue.setValue('');
        this.linkForm.controls.linkDescription.setValue('');
    }

    deleteLink(index: number) {
        const links = [...this.linksValue];
        const link = links[index];
        if (link.Id) {
            this.attributesIdsToDelete.push(link.Id);
        }
        links.splice(index, 1);
        this.setLinks(links);
    }

    async save() {
        this.disabled = true;

        const beforeSaveHook = this.data.beforeSaveHook
            ? this.data.beforeSaveHook(this.data).toPromise()
            : Promise.resolve();

        const changes = await beforeSaveHook;

        if (this.linkForm.valid) {
            this.addNewLink();
        }

        if (!changes) {
            this.processAttributes();
            return;
        }

        const parentId = _.get(changes, 'data[0].Id');
        if (parentId) {
            this.data.parameters.ParentId = parentId;
            this.addParentId(this.commentsValue, parentId);
            this.addParentId(this.linksValue, parentId);
            this.addParentId(this.assumptionsValue, parentId);
        }
        this.processAttributes();
    }

    processAttributes() {
        this.attributesToAddOrUpdate = this.linksValue.filter(link => link.IsChanged || !link.Id);

        if (this.commentComponent.isEditorEmpty && this.comment.Id) {
            this.attributesIdsToDelete.push(this.comment.Id);
        } else if (this.comment.IsChanged && !this.commentComponent.isEditorEmpty) {
            this.attributesToAddOrUpdate.push(this.comment);
        }

        this.assumptionsValue.forEach(assumption => {
            if (!assumption.isNew && assumption.IsEmpty && !assumption.IsEnabled) {
                this.attributesIdsToDelete.push(assumption.Id);
            } else if (assumption.IsChanged && (!assumption.IsEmpty || assumption.IsEnabled)) {
                this.attributesToAddOrUpdate.push(assumption);
            }
        });
    }

    callForkJoin(queries: Observable<any>[]) {
        forkJoin(queries).subscribe();
    }

    initDefaultAssumption() {}

    initDefaultComment() {}

    getAttributeOperationsObservables() {
        const queries = [];
        if (this.attributesIdsToDelete.length) {
            queries.push(this.attributeService.deleteAttributes(this.attributesIdsToDelete));
        }
        if (this.attributesToAddOrUpdate.length) {
            queries.push(this.attributeService.addOrUpdate(this.attributesToAddOrUpdate));
        }
        return queries;
    }
}
